import { Controller } from "@hotwired/stimulus"

// Generic infinite scroll for single-list project pages (Real Estate, Design,
// Bidding, Construction). Works alongside each page's own Stimulus controller.
//
// Reads the turbo frame's `src` attribute (which already contains all current
// filter/sort/search params), increments the `page` param, fetches the next
// page, and appends items to the existing `#projects-items` container.
export default class extends Controller {
  static targets = ["sentinel"]
  static values  = { frameId: String }

  connect() {
    this.page = 1
    this.done = false
    this.loading = false
    this.abortController = null

    // Throttled scroll detection (200ms, leading + trailing edge)
    this._onScroll = this._throttle(() => this._checkAndLoad(), 200)
    window.addEventListener("scroll", this._onScroll, { passive: true })

    // Safety net: scrollend catches fast scroll-and-release
    this._onScrollEnd = () => this._checkAndLoad()
    window.addEventListener("scrollend", this._onScrollEnd, { passive: true })

    // Reset on frame reload (filter/sort/search/tab/view changes)
    this._onFrameLoad = (e) => {
      const frame = this._frame()
      if (!frame || e.target !== frame) return
      this._reset()
    }
    this.element.addEventListener("turbo:frame-load", this._onFrameLoad)
  }

  disconnect() {
    window.removeEventListener("scroll", this._onScroll)
    window.removeEventListener("scrollend", this._onScrollEnd)
    this.element.removeEventListener("turbo:frame-load", this._onFrameLoad)
    this.abortController?.abort()
    clearTimeout(this._recheckTimer)
  }

  // ── Reset on frame reload ───────────────────────────────────────

  _reset() {
    this.abortController?.abort()
    this.abortController = null
    this.loading = false
    this.page = 1
    this.done = false
    this._hideSentinel()

    // After the frame finishes rendering, re-read last-page and auto-fill
    clearTimeout(this._recheckTimer)
    this._recheckTimer = setTimeout(() => {
      const container = this._itemsContainer()
      if (!container) return // non-project view (e.g. tags/portfolios)
      if (container.dataset.lastPage === "true") {
        this.done = true
        return
      }
      this._checkAndLoad(true)
    }, 100)
  }

  // ── Core loading logic ──────────────────────────────────────────

  _checkAndLoad(force = false) {
    if (this.loading || this.done) return

    const container = this._itemsContainer()
    if (!container) return

    if (container.dataset.lastPage === "true") {
      this.done = true
      this._hideSentinel()
      return
    }

    if (!force && !this._isNearBottom()) {
      this._hideSentinel()
      return
    }

    this._loadNextPage()
  }

  async _loadNextPage() {
    this.loading = true
    this._showSentinel()

    const nextPage = this.page + 1
    const wasNearBottom = this._isNearBottom()

    this.abortController = new AbortController()
    const { signal } = this.abortController

    try {
      const url = this._buildUrl(nextPage)
      if (!url) { this.done = true; return }

      const resp = await fetch(url, { headers: { Accept: "text/html" }, signal })
      const html = await resp.text()
      if (signal.aborted) return

      const doc = new DOMParser().parseFromString(html, "text/html")
      const parsed = doc.getElementById("projects-items")

      if (!parsed || parsed.children.length === 0) {
        this.done = true
        return
      }

      const container = this._itemsContainer()
      if (!container) { this.done = true; return }

      // Append new items
      while (parsed.firstChild) {
        container.appendChild(document.adoptNode(parsed.firstChild))
      }

      // Check if this was the last page
      if (parsed.dataset.lastPage === "true") {
        this.done = true
      }

      this.page = nextPage
    } catch (e) {
      if (e.name === "AbortError") return
      console.error("Project list scroll error:", e)
    } finally {
      if (!signal.aborted) {
        this.loading = false
        this.abortController = null
        this._hideSentinel()

        // Auto-fill: if user was near bottom, force another check
        if (wasNearBottom && !this.done) {
          this._checkAndLoad(true)
        }
        // Recheck after layout reflow
        clearTimeout(this._recheckTimer)
        this._recheckTimer = setTimeout(() => this._checkAndLoad(), 300)
      }
    }
  }

  // ── URL building ────────────────────────────────────────────────

  _buildUrl(page) {
    const frame = this._frame()
    if (!frame?.src) return null

    const url = new URL(frame.src, window.location.origin)
    url.searchParams.set("page", page)
    return url.toString()
  }

  // ── Helpers ─────────────────────────────────────────────────────

  _frame() {
    return document.getElementById(this.frameIdValue)
  }

  _itemsContainer() {
    return document.getElementById("projects-items")
  }

  _isNearBottom() {
    const scrollBottom = window.innerHeight + window.scrollY
    const docHeight = document.documentElement.scrollHeight
    return docHeight - scrollBottom < 300
  }

  _showSentinel() {
    if (this.hasSentinelTarget) this.sentinelTarget.classList.remove("hidden")
  }

  _hideSentinel() {
    if (this.hasSentinelTarget) this.sentinelTarget.classList.add("hidden")
  }

  _throttle(fn, ms) {
    let last = 0
    let trailing = null
    return () => {
      const now = Date.now()
      clearTimeout(trailing)
      if (now - last >= ms) {
        last = now
        fn()
      } else {
        trailing = setTimeout(() => {
          last = Date.now()
          fn()
        }, ms - (now - last))
      }
    }
  }
}
